import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
  Activity, CheckCircle2, ArrowRight, Loader2, Shield, Check
} from 'lucide-react';

const GymPlansPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (!authLoading && !user) navigate('/');
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const plans = [
    {
      id: 'annual',
      name: 'Plano Anual',
      months: 12,
      priceCash: 'R$ 2.398,80',
      priceMonthly: 'R$ 199,90',
      priceMonthlyLabel: '12x de R$ 199,90 no boleto',
      icon: <Activity className="w-7 h-7" />,
      color: 'orange',
      features: [
        'Gestão completa de alunos corporativos',
        'Check-in por QR Code e Geolocalização',
        'Registro de avaliações físicas',
        'Dashboard de engajamento',
        'Controle por empresa parceira',
        'Suporte prioritário'
      ]
    },
    {
      id: 'biennial',
      name: 'Plano 24 Meses',
      months: 24,
      priceCash: 'R$ 4.317,84',
      priceMonthly: 'R$ 359,82',
      priceMonthlyLabel: '12x de R$ 359,82 no boleto',
      discount: 10,
      popular: true,
      icon: <Shield className="w-7 h-7" />,
      color: 'emerald',
      features: [
        'Tudo do Plano Anual',
        '10% de desconto no valor total',
        'Integração com sistema de catracas',
        'Relatórios avançados de presença',
        'API de integração com empresas',
        'Gerente de conta dedicado'
      ]
    }
  ];

  const colorMap = {
    orange: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-500', hover: 'hover:bg-orange-600' },
    emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', hover: 'hover:bg-emerald-700' }
  };

  const handleSubscribe = async (plan) => {
    setSelectedPlan(plan);
    setSubmitting(true);
    try {
      await axios.post(`${API}/gyms/subscribe`, {
        plan_id: plan.id,
        payment_method: 'boleto'
      });
      toast.success('Assinatura realizada com sucesso!');
      await checkAuth();
      setDone(true);
      setTimeout(() => navigate('/gym'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao assinar');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="gym-plans-done">
        <Toaster position="top-center" />
        <div className="text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Assinatura Confirmada!
          </h1>
          <p className="text-slate-600 mb-4">Redirecionando para o dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gym-plans-page">
      <Toaster position="top-center" />
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Activity className="w-7 h-7 text-orange-500" />
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Assinatura Academia Parceira
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Escolha o plano ideal para gerenciar seus alunos corporativos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {plans.map(plan => {
            const c = colorMap[plan.color];
            return (
              <div key={plan.id} className={`bg-white rounded-2xl border ${plan.popular ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-slate-200'} p-8 relative`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                    10% DESCONTO
                  </div>
                )}
                <div className={`w-14 h-14 ${c.light} rounded-2xl flex items-center justify-center ${c.text} mb-4`}>
                  {plan.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{plan.name}</h3>

                <div className="mb-1">
                  <span className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{plan.priceCash}</span>
                  <span className="text-sm text-slate-500 ml-1">à vista</span>
                </div>
                <p className="text-sm text-slate-500 mb-6">ou {plan.priceMonthlyLabel}</p>

                <ul className="space-y-2.5 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${c.text}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan)}
                  disabled={submitting}
                  data-testid={`gym-plan-${plan.id}`}
                  className={`w-full ${c.bg} ${c.hover} text-white`}
                >
                  {submitting && selectedPlan?.id === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Assinar {plan.name}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GymPlansPage;
